import React, { useState, useEffect, useCallback } from 'react';
import '../MemoryManager.css';
import MemoryCard from './MemoryCard';
import MemoryGraph from './MemoryGraph';
import MemoryTimeline from './MemoryTimeline';
import Toast from './Toast';

// Include existing categories from your memories
const PREDEFINED_LABELS = [
  'experiences', 'preferences', 'relationships', 'memories', 'thoughts',
  'Work', 'Family', 'Personal', 'Private', 'Health', 'Finance', 
  'Education', 'Hobby', 'Travel', 'Social', 'Goals', 'Ideas'
];

const LABEL_COLORS = {
  // Memory categories (warm earth-tone palette)
  'experiences': '#3b725f',
  'preferences': '#7f5f43',
  'relationships': '#bd7b69',
  'memories': '#cccac5',
  'thoughts': '#D8BFD8',
  'ambient_capture': '#2D9CDB',
  // New label categories
  'Work': '#FF6B6B',
  'Family': '#4ECDC4',
  'Personal': '#45B7D1',
  'Private': '#96CEB4',
  'Health': '#FECA57',
  'Finance': '#48DBFB',
  'Education': '#FF9FF3',
  'Hobby': '#54A0FF',
  'Travel': '#FD79A8',
  'Social': '#A29BFE',
  'Goals': '#6C5CE7',
  'Ideas': '#FFC048'
};

function MemoryManager({ token, api, isActive, onClose }) {
  const [memories, setMemories] = useState([]);
  const [filteredMemories, setFilteredMemories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMemories, setSelectedMemories] = useState(new Set());
  const [filterLabels, setFilterLabels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [editingMemory, setEditingMemory] = useState(null);
  const [showLabelEditor, setShowLabelEditor] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [customLabel, setCustomLabel] = useState('');
  const [toast, setToast] = useState(null);
  const [allLabels, setAllLabels] = useState(new Set(PREDEFINED_LABELS));
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'graph', or 'timeline'
  const [selectedGraphMemory, setSelectedGraphMemory] = useState(null);

  // Fetch memories on mount
  useEffect(() => {
    if (isActive && token) {
      fetchMemories();
    }
  }, [isActive, token]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...memories];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(memory => 
        memory.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        memory.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (memory.notes && memory.notes.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (memory.labels && memory.labels.some(label => 
          label.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }

    // Apply label filter (checks both category and labels)
    if (filterLabels.length > 0) {
      filtered = filtered.filter(memory => {
        // Check if memory category matches any filter
        if (memory.category && filterLabels.includes(memory.category)) {
          return true;
        }
        
        // Check if memory has labels array and it contains any of the filter labels
        const memoryLabels = memory.labels || [];
        return memoryLabels.length > 0 && filterLabels.some(label => memoryLabels.includes(label));
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        break;
      case 'category':
        filtered.sort((a, b) => a.category.localeCompare(b.category));
        break;
      case 'alphabetical':
        filtered.sort((a, b) => a.text.localeCompare(b.text));
        break;
      default:
        break;
    }

    setFilteredMemories(filtered);
  }, [memories, searchQuery, filterLabels, sortBy]);

  // Extract all unique labels from memories and count them
  const [labelCounts, setLabelCounts] = useState({});
  
  useEffect(() => {
    const labels = new Set(PREDEFINED_LABELS);
    const counts = {};
    
    memories.forEach(memory => {
      // Count categories as labels too (for existing memories)
      if (memory.category && memory.category !== 'unknown') {
        labels.add(memory.category);
        counts[memory.category] = (counts[memory.category] || 0) + 1;
      }
      
      // Also count actual labels
      if (memory.labels && memory.labels.length > 0) {
        memory.labels.forEach(label => {
          labels.add(label);
          counts[label] = (counts[label] || 0) + 1;
        });
      }
    });
    
    setAllLabels(labels);
    setLabelCounts(counts);
  }, [memories]);

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${api}/retrieve_memories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setMemories(data.memories || []);
      } else {
        showToast('Failed to fetch memories', 'error');
      }
    } catch (error) {
      console.error('Error fetching memories:', error);
      showToast('Error loading memories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSelectMemory = (memoryId) => {
    const newSelected = new Set(selectedMemories);
    if (newSelected.has(memoryId)) {
      newSelected.delete(memoryId);
    } else {
      newSelected.add(memoryId);
    }
    setSelectedMemories(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    if (selectedMemories.size === filteredMemories.length) {
      setSelectedMemories(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedMemories(new Set(filteredMemories.map(m => m.id)));
      setShowBulkActions(true);
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    try {
      const response = await fetch(`${api}/delete_memory_by_id`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ memory_id: memoryId }),
      });

      if (response.ok) {
        setMemories(prev => prev.filter(m => m.id !== memoryId));
        showToast('Memory deleted successfully');
      } else {
        showToast('Failed to delete memory', 'error');
      }
    } catch (error) {
      console.error('Error deleting memory:', error);
      showToast('Error deleting memory', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMemories.size === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedMemories.size} memories? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const response = await fetch(`${api}/batch_delete_memories`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ memory_ids: Array.from(selectedMemories) }),
      });

      if (response.ok) {
        setMemories(prev => prev.filter(m => !selectedMemories.has(m.id)));
        setSelectedMemories(new Set());
        setShowBulkActions(false);
        showToast(`Deleted ${selectedMemories.size} memories`);
      } else {
        showToast('Failed to delete memories', 'error');
      }
    } catch (error) {
      console.error('Error deleting memories:', error);
      showToast('Error deleting memories', 'error');
    }
  };

  const handleEditMemory = (memory) => {
    setEditingMemory(memory);
    setSelectedLabels(memory.labels || []);
    setShowLabelEditor(true);
  };

  const handleSaveLabels = async () => {
    if (!editingMemory) return;

    try {
      const response = await fetch(`${api}/update_memory_labels`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          memory_id: editingMemory.id,
          labels: selectedLabels,
          category: editingMemory.category,
          notes: editingMemory.notes,
        }),
      });

      if (response.ok) {
        setMemories(prev =>
          prev.map(m =>
            m.id === editingMemory.id
              ? { ...m, labels: selectedLabels, notes: editingMemory.notes }
              : m
          )
        );
        setShowLabelEditor(false);
        setEditingMemory(null);
        showToast('Memory updated successfully');
      } else {
        showToast('Failed to update memory', 'error');
      }
    } catch (error) {
      console.error('Error updating memory:', error);
      showToast('Error updating memory', 'error');
    }
  };

  const handleBulkLabel = async (action) => {
    if (selectedMemories.size === 0 || selectedLabels.length === 0) return;

    try {
      const response = await fetch(`${api}/batch_update_labels`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          memory_ids: Array.from(selectedMemories),
          labels: selectedLabels,
          action: action, // 'add', 'remove', or 'replace'
        }),
      });

      if (response.ok) {
        await fetchMemories(); // Refresh memories
        setSelectedMemories(new Set());
        setShowBulkActions(false);
        setSelectedLabels([]);
        showToast(`Updated labels for ${selectedMemories.size} memories`);
      } else {
        showToast('Failed to update labels', 'error');
      }
    } catch (error) {
      console.error('Error updating labels:', error);
      showToast('Error updating labels', 'error');
    }
  };

  const handleAddCustomLabel = () => {
    if (customLabel && !selectedLabels.includes(customLabel)) {
      setSelectedLabels([...selectedLabels, customLabel]);
      setCustomLabel('');
    }
  };

  const toggleLabelSelection = (label) => {
    if (selectedLabels.includes(label)) {
      setSelectedLabels(selectedLabels.filter(l => l !== label));
    } else {
      setSelectedLabels([...selectedLabels, label]);
    }
  };

  const toggleFilterLabel = (label) => {
    if (filterLabels.includes(label)) {
      setFilterLabels(filterLabels.filter(l => l !== label));
    } else {
      setFilterLabels([...filterLabels, label]);
    }
  };

  if (!isActive) return null;

  return (
    <div className="memory-manager-overlay">
      <div className="memory-manager">
        <div className="memory-manager-header">
          <h2>Memory Manager</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="memory-stats">
          <div className="stat">
            <span className="stat-value">{memories.length}</span>
            <span className="stat-label">Total Memories</span>
          </div>
          <div className="stat">
            <span className="stat-value">{filterLabels.length}</span>
            <span className="stat-label">Active Filters</span>
          </div>
          <div className="stat">
            <span className="stat-value">{selectedMemories.size}</span>
            <span className="stat-label">Selected</span>
          </div>
        </div>

        <div className="memory-controls">
          <div className="view-toggle">
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <span style={{ marginRight: '5px' }}>⊞</span> Memories
            </button>
            <button 
              className={viewMode === 'graph' ? 'active' : ''}
              onClick={() => setViewMode('graph')}
            >
              <span style={{ marginRight: '5px' }}>✧</span> Essence
            </button>
            <button 
              className={viewMode === 'timeline' ? 'active' : ''}
              onClick={() => setViewMode('timeline')}
            >
              <span style={{ marginRight: '5px' }}>⏱</span> Timeline
            </button>
          </div>

          <div className="search-bar">
            <input
              type="text"
              placeholder="Search memories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          {viewMode === 'grid' && (
            <div className="filter-sort-controls">
              <div className="sort-control">
                <label>Sort by:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="category">Category</option>
                  <option value="alphabetical">Alphabetical</option>
                </select>
              </div>

              <button 
                className="select-all-button"
                onClick={handleSelectAll}
              >
                {selectedMemories.size === filteredMemories.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          )}

          <div className="label-filters">
            <span className="filter-label">Filter by labels:</span>
            <div className="label-chips">
              {Array.from(allLabels).map(label => {
                const count = labelCounts[label] || 0;
                return (
                  <button
                    key={label}
                    className={`label-chip ${filterLabels.includes(label) ? 'active' : ''} ${count === 0 ? 'empty' : ''}`}
                    onClick={() => toggleFilterLabel(label)}
                    style={{
                      backgroundColor: filterLabels.includes(label) 
                        ? (LABEL_COLORS[label] || '#666')
                        : 'transparent',
                      borderColor: LABEL_COLORS[label] || '#666',
                      color: filterLabels.includes(label) ? 'white' : (LABEL_COLORS[label] || '#666'),
                      opacity: count === 0 ? 0.5 : 1
                    }}
                    title={count > 0 ? `${count} memories` : 'No memories with this label yet'}
                  >
                    {label} {count > 0 && <span style={{ marginLeft: '4px', fontSize: '11px' }}>({count})</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {showBulkActions && (
          <div className="bulk-actions">
            <h3>Bulk Actions ({selectedMemories.size} selected)</h3>
            <div className="bulk-buttons">
              <button 
                className="bulk-delete-button"
                onClick={handleBulkDelete}
              >
                Delete Selected
              </button>
              <button 
                className="bulk-label-button"
                onClick={() => setShowLabelEditor(true)}
              >
                Manage Labels
              </button>
            </div>
          </div>
        )}

        {viewMode === 'timeline' ? (
          <div className="memory-timeline-view" style={{ padding: '0 15px', overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div className="loading-message">Loading memories...</div>
            ) : (
              <MemoryTimeline
                memories={searchQuery || filterLabels.length > 0 ? filteredMemories : [...memories].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))}
                onMemoryClick={(memory) => handleEditMemory(memory)}
              />
            )}
          </div>
        ) : viewMode === 'graph' ? (
          <div className="memory-graph-view">
            {loading ? (
              <div className="loading-message">Loading memories...</div>
            ) : memories.length > 0 ? (
              <MemoryGraph 
                memories={searchQuery || filterLabels.length > 0 ? filteredMemories : memories}
                onMemoryClick={(memory) => {
                  setSelectedGraphMemory(memory);
                  handleEditMemory(memory);
                }}
                selectedMemory={selectedGraphMemory}
              />
            ) : (
              <div className="no-memories">No memory essence to reveal yet</div>
            )}
          </div>
        ) : (
          <div className="memories-grid">
            {loading ? (
              <div className="loading-message">Loading memories...</div>
            ) : filteredMemories.length > 0 ? (
              filteredMemories.map((memory, index) => (
              <div key={memory.id} className="memory-item">
                <div className="memory-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedMemories.has(memory.id)}
                    onChange={() => handleSelectMemory(memory.id)}
                  />
                </div>
                <div className="memory-content">
                  <div className="memory-header">
                    <span className="memory-category" 
                      style={{ 
                        backgroundColor: LABEL_COLORS[memory.category] || 'rgba(59, 114, 95, 0.1)',
                        color: 'white' 
                      }}>
                      {memory.category}
                    </span>
                    <span className="memory-date">
                      {new Date(memory.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="memory-text">{memory.text}</p>
                  {/* Show additional labels if any */}
                  {memory.labels && memory.labels.length > 0 && (
                    <div className="memory-labels">
                      {memory.labels.map(label => (
                        <span 
                          key={label} 
                          className="memory-label"
                          style={{ backgroundColor: LABEL_COLORS[label] || '#666' }}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                  {memory.notes && (
                    <p className="memory-notes">{memory.notes}</p>
                  )}
                  <div className="memory-actions">
                    <button 
                      className="edit-button"
                      onClick={() => handleEditMemory(memory)}
                    >
                      Edit Labels
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteMemory(memory.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-memories">
              {filterLabels.length > 0 ? (
                <div>
                  <p>No memories found with the selected labels.</p>
                  <p style={{ fontSize: '14px', marginTop: '10px', opacity: 0.8 }}>
                    Tip: Your existing memories don't have labels yet. 
                    Edit your memories to add labels first!
                  </p>
                  <button 
                    onClick={() => setFilterLabels([])}
                    style={{
                      marginTop: '15px',
                      padding: '8px 20px',
                      background: 'rgba(59, 114, 95, 0.3)',
                      border: '1px solid rgba(59, 114, 95, 0.5)',
                      borderRadius: '20px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              ) : searchQuery ? (
                'No memories match your search'
              ) : (
                'No memories yet'
              )}
            </div>
          )}
          </div>
        )}

        {showLabelEditor && (
          <div className="label-editor-overlay">
            <div className="label-editor">
              <h3>{editingMemory ? 'Edit Memory Labels' : 'Bulk Label Update'}</h3>
              
              <div className="predefined-labels">
                <h4>Select Labels:</h4>
                <div className="label-grid">
                  {PREDEFINED_LABELS.map(label => (
                    <button
                      key={label}
                      className={`label-option ${selectedLabels.includes(label) ? 'selected' : ''}`}
                      onClick={() => toggleLabelSelection(label)}
                      style={{
                        backgroundColor: selectedLabels.includes(label) 
                          ? (LABEL_COLORS[label] || '#666')
                          : 'transparent',
                        borderColor: LABEL_COLORS[label] || '#666',
                        color: selectedLabels.includes(label) ? 'white' : (LABEL_COLORS[label] || '#666')
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="custom-label-input">
                <input
                  type="text"
                  placeholder="Add custom label..."
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCustomLabel()}
                />
                <button onClick={handleAddCustomLabel}>Add</button>
              </div>

              {editingMemory && (
                <div className="notes-input">
                  <h4>Notes:</h4>
                  <textarea
                    value={editingMemory.notes || ''}
                    onChange={(e) => setEditingMemory({...editingMemory, notes: e.target.value})}
                    placeholder="Add notes about this memory..."
                    rows={3}
                  />
                </div>
              )}

              <div className="label-editor-actions">
                {editingMemory ? (
                  <>
                    <button onClick={handleSaveLabels}>Save</button>
                    <button onClick={() => {
                      setShowLabelEditor(false);
                      setEditingMemory(null);
                      setSelectedLabels([]);
                    }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleBulkLabel('add')}>Add Labels</button>
                    <button onClick={() => handleBulkLabel('replace')}>Replace Labels</button>
                    <button onClick={() => handleBulkLabel('remove')}>Remove Labels</button>
                    <button onClick={() => {
                      setShowLabelEditor(false);
                      setSelectedLabels([]);
                    }}>Cancel</button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {toast && (
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)} 
          />
        )}
      </div>
    </div>
  );
}

export default MemoryManager;
